import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import Select from 'react-select';
import { Modal, OverlayTrigger, Popover, Spinner } from 'react-bootstrap';
import { File, Info, MessageSquare, Paperclip, X } from 'lucide-react';
import { toast } from 'sonner';
import AccountPickerScope from '../../Common/AccountPickerScope';
import AccountSelect from '../../Common/AccountSelect';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { amountToCents, sanitizeAmountInput } from '../manualBookkeepingUtils';
import './guided-details-step.css';

/*
 * Detaljer — the step where the user answers the template's questions.
 *
 * Everything the template does not ask for has a sensible default: the
 * description is the template's name, the date is today, reference, underlag
 * and kommentar are optional and folded away behind text actions. A novice
 * should be able to type one amount and go on to Granska.
 *
 * State lives in `entry` (the wizard's), written through `onChange`; the only
 * local state is what the user types into numeric fields before it parses,
 * which fields they have touched, and the attachment upload/preview plumbing.
 */

const MAX_FILES = 10;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_TYPES = [
  'image/jpeg', 'image/jpg', 'image/png',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];

const CURRENCY_TYPES = ['currency', 'currency_gross', 'currency_net'];
export const isNumericType = (t) => CURRENCY_TYPES.includes(t) || t === 'number' || t === 'integer';
const isCurrencyType = (t) => CURRENCY_TYPES.includes(t);

// Filter the tenant accounts list by a new-shape input.filter
// expression. Supports `class:N`, `class:N-M`, `class:1,2,3`.
// Unknown / missing filter → return the full list unchanged.
const filterAccountsByExpression = (accounts, filter) => {
  if (!Array.isArray(accounts) || accounts.length === 0) return [];
  if (!filter || typeof filter !== 'string') return accounts;
  const m = /^class:(.+)$/.exec(filter.trim());
  if (!m) return accounts;
  const spec = m[1].trim();
  const firstDigit = (acct) => {
    const s = (acct && (acct.accountNumber || acct.value)) || '';
    return typeof s === 'string' && s.length > 0 ? s[0] : '';
  };
  if (/^\d(\s*,\s*\d)+$/.test(spec)) {
    const set = new Set(spec.split(',').map((s) => s.trim()));
    return accounts.filter((a) => set.has(firstDigit(a)));
  }
  const range = /^(\d)\s*-\s*(\d)$/.exec(spec);
  if (range) {
    const lo = parseInt(range[1], 10);
    const hi = parseInt(range[2], 10);
    return accounts.filter((a) => {
      const d = parseInt(firstDigit(a), 10);
      return Number.isFinite(d) && d >= lo && d <= hi;
    });
  }
  if (/^\d$/.test(spec)) {
    return accounts.filter((a) => firstDigit(a) === spec);
  }
  return accounts;
};

// One-line VAT-rate hint under currency_gross/_net inputs that declare a
// literal vat_rate. Keeps the template's Skatteverket grounding visible.
const vatRateHint = (input) => {
  if (!input || typeof input.vat_rate !== 'number') return null;
  if (input.vat_rate === 25) return 'Standardmoms — varor och tjänster i allmänhet';
  if (input.vat_rate === 12) return 'Reducerad moms — livsmedel, hotell, restaurang';
  if (input.vat_rate === 6) return 'Reducerad moms — böcker, persontrafik, kultur';
  if (input.vat_rate === 0) return 'Momsfritt';
  return null;
};

const sek = new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const plain = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 2 });
const formatExample = (example) => (typeof example === 'number' ? plain.format(example) : String(example));

// What the entered amount means in VAT terms, so a novice sees the split
// before Granska: "varav moms" for gross inputs, "moms tillkommer" for net.
const vatBreakdown = (input, amount) => {
  if (!input || typeof input.vat_rate !== 'number' || !(amount > 0)) return null;
  const rate = input.vat_rate;
  if (rate === 0) return 'Momsfritt';
  if (input.type === 'currency_gross') {
    const vat = amount - amount / (1 + rate / 100);
    return <>varav moms {rate} %: <strong>{sek.format(vat)} kr</strong></>;
  }
  if (input.type === 'currency_net') {
    const vat = amount * (rate / 100);
    return <>moms {rate} % tillkommer: <strong>{sek.format(vat)} kr</strong></>;
  }
  return null;
};

// Normalize the template's inputs across both shapes. Legacy templates carry
// `parameters[]` + `transactions[].is_user_defined`; new-shape templates
// carry `body.inputs[]`, already in the unified form.
export const getEffectiveInputs = (templateData) => {
  if (!templateData) return [];
  if (templateData.body && Array.isArray(templateData.body.inputs)) {
    return templateData.body.inputs;
  }
  const out = [];
  if (Array.isArray(templateData.parameters)) {
    for (const p of templateData.parameters) {
      let type = 'text';
      if (p.parameter_type === 'Number') type = 'currency';
      else if (p.parameter_type === 'Date') type = 'date';
      out.push({ name: p.parameter_name, label: p.parameter_label, type, required: !!p.required });
    }
  }
  if (Array.isArray(templateData.transactions)) {
    templateData.transactions
      .filter((t) => t.is_user_defined)
      .forEach((t, index) => {
        out.push({
          name: `user_account_${index}`,
          label: `Välj konto ${t.debit_formula ? '(Debet)' : '(Kredit)'}`,
          type: 'account_picker',
          required: true,
        });
      });
  }
  return out;
};

// Legacy templates keep user-chosen accounts in a separate bucket; new-shape
// templates put everything in `parameters`.
const isLegacyUserAccount = (entry, input) =>
  !!(entry.templateData && entry.templateData.parameters) && input.name.startsWith('user_account_');

const readInputValue = (entry, input) => (isLegacyUserAccount(entry, input)
  ? entry.userDefinedAccounts?.[input.name]
  : entry.parameters?.[input.name]);

const writeInputValue = (entry, input, value) => (isLegacyUserAccount(entry, input)
  ? { ...entry, userDefinedAccounts: { ...(entry.userDefinedAccounts || {}), [input.name]: value } }
  : { ...entry, parameters: { ...(entry.parameters || {}), [input.name]: value } });

const isBlank = (v) => v === undefined || v === null || v === '';

// Every required thing the step still needs, with the message shown under
// the field. The wizard uses this to stop "Granska bokföring" and name the
// first gap; the step uses it to mark fields.
export const getMissingRequired = (entry) => {
  const missing = [];
  const description = (entry.description || '').trim();
  if (!description) missing.push({ name: 'description', label: 'Beskrivning', message: 'Skriv en kort beskrivning.' });
  else if (description.length < 3) missing.push({ name: 'description', label: 'Beskrivning', message: 'Beskrivningen måste vara minst 3 tecken.' });
  if (!entry.date) missing.push({ name: 'date', label: 'Datum', message: 'Ange ett datum.' });

  for (const input of getEffectiveInputs(entry.templateData)) {
    if (!input.required) continue;
    const v = readInputValue(entry, input);
    let message = null;
    if (isBlank(v)) {
      if (isCurrencyType(input.type)) message = 'Ange ett belopp.';
      else if (input.type === 'account_picker') message = 'Välj ett konto.';
      else if (input.type === 'select') message = 'Välj ett alternativ.';
      else message = 'Fyll i fältet.';
    } else if ((isCurrencyType(input.type) || input.type === 'number') && !(Number(v) > 0) && input.default === undefined) {
      // A template that defaults an amount to 0 ("eventuellt tillägg") means
      // 0 is a real answer; only amounts with no default must be positive.
      message = 'Beloppet måste vara större än 0.';
    }
    if (message) missing.push({ name: input.name, label: input.label, message });
  }
  return missing;
};

// Numeric inputs are typed as text (Swedish decimal comma, no spinner) and
// parsed into numbers for the entry.
// A number coming back from the entry (returning from Granska, a template
// default) is shown the way a Swede would write it; what the user types is
// kept exactly as typed.
const draftFromValue = (value, type = 'currency') => {
  if (isBlank(value)) return '';
  if (typeof value !== 'number') return String(value);
  if (type === 'integer') return String(value);
  return isCurrencyType(type) ? sek.format(value) : plain.format(value);
};

const parseDraft = (type, draft) => {
  if (draft === '') return '';
  if (type === 'integer') {
    const n = parseInt(String(draft).replace(/\D/g, ''), 10);
    return Number.isFinite(n) ? n : '';
  }
  const cents = amountToCents(draft);
  return cents === null ? '' : cents / 100;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'kB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(1))} ${units[i]}`;
};

const canPreviewFile = (attachment) => {
  const type = attachment?.type || '';
  if (type.startsWith('image/') || type === 'application/pdf') return true;
  const ext = (attachment?.name || '').toLowerCase().split('.').pop();
  return ['jpg', 'jpeg', 'png', 'gif', 'pdf'].includes(ext);
};

// react-select controls sit next to .form-control inputs; give them the
// same 38px box, radius and border so the grid reads as one form.
const selectStyles = (isInvalid) => ({
  control: (base, state) => ({
    ...base,
    minHeight: '38px',
    borderRadius: '8px',
    fontSize: '0.875rem',
    borderColor: isInvalid ? '#d92d20' : state.isFocused ? '#44814E' : '#aeb6c2',
    boxShadow: isInvalid
      ? 'inset 0 0 0 1px #d92d20'
      : state.isFocused ? '0 0 0 3px rgba(68, 129, 78, 0.20)' : 'none',
    '&:hover': { borderColor: isInvalid ? '#d92d20' : state.isFocused ? '#44814E' : '#8793a1' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 0.25rem 0 0.75rem' }),
  placeholder: (base) => ({ ...base, color: '#7e8792' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
});

const DetailsEntryStep = ({
  // The full chart, for resolving a stored selection's label whatever the
  // browse scope shows (review 2026-09-05, F11); `accounts` is the picker view.
  allAccounts = null,
  entry,
  onChange,
  companyId,
  accounts = [],
  accountScope = null,
  accountsLoading = false,
}) => {
  const inputs = useMemo(() => getEffectiveInputs(entry.templateData), [entry.templateData]);
  const missing = useMemo(() => getMissingRequired(entry), [entry]);
  const errorFor = (name) => missing.find((m) => m.name === name)?.message || null;

  const [touched, setTouched] = useState({});
  const markTouched = (name) => setTouched((prev) => (prev[name] ? prev : { ...prev, [name]: true }));
  const showError = (name) => (touched[name] || entry.showDetailsErrors) ? errorFor(name) : null;

  // Text the user is typing into numeric fields, kept as typed (with a
  // decimal comma) while the parsed number goes into the entry.
  const [drafts, setDrafts] = useState(() => {
    const initial = {};
    for (const input of getEffectiveInputs(entry.templateData)) {
      if (isNumericType(input.type)) initial[input.name] = draftFromValue(readInputValue(entry, input), input.type);
    }
    return initial;
  });
  useEffect(() => {
    // A different template means different fields; start the drafts over.
    const next = {};
    for (const input of getEffectiveInputs(entry.templateData)) {
      if (isNumericType(input.type)) next[input.name] = draftFromValue(readInputValue(entry, input), input.type);
    }
    setDrafts(next);
    setTouched({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.template]);

  // The description defaults to the template's name so the step never
  // starts with a required field empty.
  useEffect(() => {
    const name = entry.templateData?.template_name;
    if (!entry.description && name) {
      onChange((prev) => ({ ...prev, description: name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.template]);

  const update = useCallback((patch) => {
    onChange((prev) => ({ ...prev, ...patch }));
  }, [onChange]);

  const setInputValue = (input, value) => {
    onChange((prev) => writeInputValue(prev, input, value));
  };

  const handleNumericChange = (input, raw) => {
    const draft = input.type === 'integer' ? String(raw).replace(/\D/g, '') : sanitizeAmountInput(raw);
    setDrafts((prev) => ({ ...prev, [input.name]: draft }));
    setInputValue(input, parseDraft(input.type, draft));
  };

  // ---- Attachments -------------------------------------------------------

  const [attachments, setAttachments] = useState(entry.attachments || []);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showAttachments, setShowAttachments] = useState((entry.attachments || []).length > 0);
  const [showNotes, setShowNotes] = useState(!!entry.notes);
  const [preview, setPreview] = useState(null); // { attachment, url, loading }

  useEffect(() => {
    if (entry.attachments && entry.attachments !== attachments) {
      setAttachments(entry.attachments);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.attachments]);

  const commitAttachments = (next) => {
    setAttachments(next);
    onChange((prev) => ({ ...prev, attachments: next }));
  };

  const handleFileUpload = async (files) => {
    const fileArray = Array.from(files || []);
    if (fileArray.length === 0) return;

    if (attachments.length + fileArray.length > MAX_FILES) {
      toast.error(`Högst ${MAX_FILES} underlag per verifikat. Du har ${attachments.length} och försöker lägga till ${fileArray.length}.`);
      return;
    }

    const validFiles = fileArray.filter((file) => {
      if (file.size > MAX_FILE_BYTES) {
        toast.error(`${file.name} är större än 10 MB.`);
        return false;
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name} har en filtyp som inte stöds. Använd PDF, bild, Word eller text.`);
        return false;
      }
      if (attachments.some((att) => att.name === file.name && att.size === file.size)) {
        toast.error(`${file.name} är redan tillagd.`);
        return false;
      }
      return true;
    });
    if (validFiles.length === 0) return;

    setUploadingCount((n) => n + validFiles.length);
    const results = await Promise.all(validFiles.map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const response = await axios.post(
          getApiUrl(`${companyId}/accounting/verifications/attachments`),
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true },
        );
        if (!response.data.success) throw new Error(response.data.message || 'Uppladdningen misslyckades');
        const a = response.data.attachment;
        return {
          id: a.attachmentId,
          attachmentId: a.attachmentId,
          name: a.fileName,
          size: a.fileSize,
          type: a.fileType,
          url: a.fileUrl,
          blob_name: a.blobName,
          blobName: a.blobName,
          preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        };
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        toast.error(`${file.name} kunde inte laddas upp: ${error.response?.data?.message || error.message}`);
        return null;
      } finally {
        setUploadingCount((n) => Math.max(0, n - 1));
      }
    }));

    const uploaded = results.filter(Boolean);
    if (uploaded.length > 0) {
      commitAttachments([...attachments, ...uploaded]);
      toast.success(uploaded.length === 1 ? 'Underlaget är tillagt.' : `${uploaded.length} underlag är tillagda.`);
    }
  };

  const removeAttachment = async (attachment) => {
    try {
      await axios.delete(
        getApiUrl(`${companyId}/accounting/verifications/attachments/${attachment.attachmentId || attachment.id}`),
        { withCredentials: true },
      );
      if (attachment.preview) URL.revokeObjectURL(attachment.preview);
      commitAttachments(attachments.filter((att) => att.id !== attachment.id));
      if (preview?.attachment?.id === attachment.id) closePreview();
    } catch (error) {
      console.error('Error removing attachment:', error);
      toast.error('Underlaget kunde inte tas bort. Försök igen.');
    }
  };

  const openPreview = async (attachment) => {
    if (!canPreviewFile(attachment)) return;
    setPreview({ attachment, url: null, loading: true });
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/attachments/${attachment.attachmentId || attachment.id}`),
        { responseType: 'blob', withCredentials: true, timeout: 15000 },
      );
      const url = response.data && response.data.size > 0 ? URL.createObjectURL(response.data) : null;
      setPreview((current) => (current?.attachment?.id === attachment.id ? { attachment, url, loading: false } : current));
    } catch (error) {
      console.error('Error fetching attachment for preview:', error);
      setPreview((current) => (current?.attachment?.id === attachment.id ? { attachment, url: null, loading: false } : current));
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };

  // ---- Render ------------------------------------------------------------

  if (accountsLoading) {
    return (
      <div className="guided-details__loading" role="status">
        <Spinner animation="border" size="sm" />
        Laddar konton…
      </div>
    );
  }

  const templateName = entry.templateData?.template_name;
  const templateDescription = entry.templateData?.description;
  const uploading = uploadingCount > 0;

  const renderLabel = (id, label, required) => (
    <label htmlFor={id} className="form-label">
      {label}
      {required
        ? <span className="text-danger"> *</span>
        : <span className="guided-details__optional">Valfritt</span>}
    </label>
  );

  const renderError = (name) => {
    const message = showError(name);
    return message ? <div className="guided-details__error" role="alert">{message}</div> : null;
  };

  const renderTemplateInput = (input) => {
    const name = input.name;
    const value = readInputValue(entry, input);
    const invalid = !!showError(name);
    const wide = !isNumericType(input.type) && input.type !== 'date';

    let control;
    if (input.type === 'account_picker') {
      const browse = filterAccountsByExpression(accounts, input.filter);
      const searchable = filterAccountsByExpression(allAccounts || accounts, input.filter);
      const selected = browse.find((acc) => acc.value === value)
        || (allAccounts || []).find((acc) => acc.value === value)
        || null;
      control = (
        <>
          <AccountSelect
            inputId={name}
            name={name}
            value={selected}
            onChange={(opt) => { markTouched(name); setInputValue(input, opt ? opt.value : ''); }}
            onBlur={() => markTouched(name)}
            options={browse}
            allOptions={searchable}
            isClearable
            styles={selectStyles(invalid)}
          />
          {accountScope && <AccountPickerScope {...accountScope} />}
        </>
      );
    } else if (input.type === 'select') {
      const opts = (input.options || []).map((o) => ({ value: o.value, label: o.label }));
      control = (
        <Select
          inputId={name}
          name={name}
          value={opts.find((o) => o.value === value) || null}
          onChange={(opt) => { markTouched(name); setInputValue(input, opt ? opt.value : ''); }}
          onBlur={() => markTouched(name)}
          options={opts}
          placeholder="Välj…"
          isClearable={!input.required}
          isSearchable={opts.length > 6}
          theme={brandSelectTheme}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          styles={selectStyles(invalid)}
        />
      );
    } else if (isNumericType(input.type)) {
      const draft = drafts[name] ?? draftFromValue(value, input.type);
      const currency = isCurrencyType(input.type);
      const hint = vatBreakdown(input, Number(value)) || vatRateHint(input);
      control = (
        <>
          <div className={currency ? 'guided-details__amount' : undefined}>
            <input
              id={name}
              name={name}
              type="text"
              inputMode={input.type === 'integer' ? 'numeric' : 'decimal'}
              className={`form-control ${invalid ? 'is-invalid' : ''}`}
              value={draft}
              placeholder={input.example !== undefined ? `t.ex. ${formatExample(input.example)}` : (currency ? '0,00' : '')}
              onChange={(e) => handleNumericChange(input, e.target.value)}
              onBlur={() => markTouched(name)}
              autoComplete="off"
            />
            {currency && <span className="guided-details__unit">kr</span>}
          </div>
          {hint && !invalid && <div className="guided-details__hint">{hint}</div>}
        </>
      );
    } else {
      control = (
        <input
          id={name}
          name={name}
          type={input.type === 'date' ? 'date' : 'text'}
          className={`form-control ${invalid ? 'is-invalid' : ''}`}
          value={isBlank(value) ? '' : value}
          placeholder={input.example ? `t.ex. ${input.example}` : ''}
          onChange={(e) => setInputValue(input, e.target.value)}
          onBlur={() => markTouched(name)}
        />
      );
    }

    return (
      <div key={name} className={`guided-details__field ${wide ? 'guided-details__field--wide' : ''}`}>
        {renderLabel(name, input.label, input.required)}
        {control}
        {renderError(name)}
      </div>
    );
  };

  const dateValue = entry.date ? format(entry.date, 'yyyy-MM-dd') : '';
  const attachmentActionLabel = showAttachments
    ? 'Dölj underlag'
    : attachments.length > 0 ? 'Visa underlag' : 'Lägg till underlag';
  const notesActionLabel = showNotes
    ? 'Dölj kommentar'
    : entry.notes ? 'Visa kommentar' : 'Lägg till kommentar';

  return (
    <div className="guided-details">
      <section className="guided-details__section" aria-labelledby="guided-details-basics-heading">
        <div className="guided-details__section-heading">
          <h3 id="guided-details-basics-heading" className="guided-details__section-title">Grunduppgifter</h3>
        </div>
        <div className="guided-details__grid">
          <div className="guided-details__field guided-details__field--wide">
            {renderLabel('guided-details-description', 'Beskrivning', true)}
            <input
              id="guided-details-description"
              type="text"
              className={`form-control ${showError('description') ? 'is-invalid' : ''}`}
              value={entry.description || ''}
              placeholder={templateName || 'Vad gäller verifikatet?'}
              onChange={(e) => update({ description: e.target.value })}
              onBlur={() => markTouched('description')}
            />
            {renderError('description')}
          </div>

          <div className="guided-details__field">
            {renderLabel('guided-details-date', 'Datum', true)}
            <input
              id="guided-details-date"
              type="date"
              className={`form-control ${showError('date') ? 'is-invalid' : ''}`}
              value={dateValue}
              onChange={(e) => {
                const next = e.target.value ? new Date(`${e.target.value}T00:00:00`) : null;
                update({ date: next && !Number.isNaN(next.getTime()) ? next : null });
              }}
              onBlur={() => markTouched('date')}
            />
            {renderError('date')}
          </div>

          <div className="guided-details__field">
            {renderLabel('guided-details-reference', 'Referens', false)}
            <input
              id="guided-details-reference"
              type="text"
              className="form-control"
              value={entry.reference || ''}
              placeholder="Kvittonummer, fakturanummer…"
              onChange={(e) => update({ reference: e.target.value })}
            />
          </div>
        </div>
      </section>

      {inputs.length > 0 && (
        <section className="guided-details__section" aria-labelledby="guided-details-template-heading">
          <div className="guided-details__section-heading">
            <h3 id="guided-details-template-heading" className="guided-details__section-title">
              {templateName || 'Uppgifter'}
              {templateDescription && (
                // The template's full description — legal grounding, edge
                // cases — on hover, so the form itself stays just the fields.
                <OverlayTrigger
                  trigger={['hover', 'focus']}
                  placement="right"
                  overlay={(
                    <Popover id="guided-details-template-description" className="guided-details__popover" style={{ maxWidth: 380 }}>
                      <Popover.Header as="h4" style={{ fontSize: '0.8125rem' }}>Om mallen</Popover.Header>
                      <Popover.Body style={{ fontSize: '0.8125rem', lineHeight: 1.55 }}>{templateDescription}</Popover.Body>
                    </Popover>
                  )}
                >
                  <button type="button" className="guided-details__info" aria-label={`Om mallen ${templateName || ''}`.trim()}>
                    <Info size={15} />
                  </button>
                </OverlayTrigger>
              )}
            </h3>
          </div>
          <div className="guided-details__grid">
            {inputs.map(renderTemplateInput)}
          </div>
        </section>
      )}

      <div className="guided-details__optional-actions" aria-label="Valfria uppgifter">
        <button
          type="button"
          className="guided-details__text-action"
          aria-expanded={showAttachments}
          aria-controls="guided-details-attachments"
          onClick={() => setShowAttachments((v) => !v)}
          disabled={uploading}
        >
          <Paperclip size={15} /> {attachmentActionLabel}
          {attachments.length > 0 && <span className="guided-details__count">{attachments.length}</span>}
        </button>
        <button
          type="button"
          className="guided-details__text-action"
          aria-expanded={showNotes}
          aria-controls="guided-details-notes"
          onClick={() => setShowNotes((v) => !v)}
        >
          <MessageSquare size={15} /> {notesActionLabel}
        </button>
      </div>

      {showAttachments && (
        <section
          id="guided-details-attachments"
          className={`guided-details__optional-section ${isDragOver ? 'is-drag-over' : ''}`}
          aria-labelledby="guided-details-attachments-heading"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="guided-details__section-heading">
            <div>
              <h3 id="guided-details-attachments-heading" className="guided-details__section-title">Underlag</h3>
              <p className="guided-details__section-hint">Lägg till kvitto, faktura eller annat underlag. Du kan också släppa filer här.</p>
            </div>
            <span className="guided-details__optional">Valfritt</span>
          </div>
          <div className="guided-details__attachments">
            {attachments.map((attachment) => {
              const previewable = canPreviewFile(attachment);
              return (
                <div key={attachment.id} className="guided-details__attachment">
                  <button
                    type="button"
                    className="guided-details__attachment-main"
                    onClick={() => openPreview(attachment)}
                    disabled={!previewable}
                    title={previewable ? 'Förhandsgranska underlag' : attachment.name}
                  >
                    <span className="guided-details__file-icon">
                      {attachment.preview ? <img src={attachment.preview} alt="" /> : <File size={17} />}
                    </span>
                    <span className="guided-details__file-copy">
                      <span className="guided-details__file-name">{attachment.name}</span>
                      <span className="guided-details__file-size">{formatFileSize(attachment.size)}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="guided-details__attachment-remove"
                    onClick={() => removeAttachment(attachment)}
                    aria-label={`Ta bort ${attachment.name}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
            {attachments.length < MAX_FILES && (
              <>
                <input
                  type="file"
                  id="guided-details-file-upload"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt"
                  className="visually-hidden"
                  disabled={uploading}
                  onChange={(e) => { handleFileUpload(e.target.files); e.target.value = ''; }}
                />
                <label htmlFor="guided-details-file-upload" className={`guided-details__upload ${uploading ? 'is-disabled' : ''}`}>
                  {uploading ? <Spinner animation="border" size="sm" /> : <Paperclip size={16} />}
                  <span>{uploading ? 'Laddar upp…' : 'Lägg till underlag'}</span>
                  <small>PDF, bild, Word eller text · max 10 MB</small>
                </label>
              </>
            )}
          </div>
        </section>
      )}

      {showNotes && (
        <section
          id="guided-details-notes"
          className="guided-details__optional-section"
          aria-labelledby="guided-details-notes-heading"
        >
          <div className="guided-details__section-heading">
            <div>
              <h3 id="guided-details-notes-heading" className="guided-details__section-title">Kommentar</h3>
              <p className="guided-details__section-hint">Något du vill minnas om händelsen. Syns bara internt.</p>
            </div>
            <span className="guided-details__optional">Valfritt</span>
          </div>
          <textarea
            id="guided-details-notes-field"
            className="form-control"
            rows={3}
            value={entry.notes || ''}
            placeholder="Till exempel vad inköpet gällde eller vem som betalade."
            aria-label="Kommentar"
            onChange={(e) => update({ notes: e.target.value })}
          />
        </section>
      )}

      <Modal show={Boolean(preview)} onHide={closePreview} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title as="h3" style={{ fontSize: '1rem' }}>{preview?.attachment?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="guided-details__preview-body">
          {preview?.loading ? (
            <Spinner animation="border" role="status"><span className="visually-hidden">Laddar…</span></Spinner>
          ) : !preview?.url ? (
            <p className="guided-details__hint">Underlaget kunde inte visas här.</p>
          ) : preview.attachment.type === 'application/pdf' || /\.pdf$/i.test(preview.attachment.name || '') ? (
            <iframe src={preview.url} title={preview.attachment.name} />
          ) : (
            <img src={preview.url} alt={preview.attachment.name} />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DetailsEntryStep;
